import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import * as Constants from "../../constants/constants";

/* ---------- helpers ---------- */
const blankItem = { desc: "", qty: 1, rate: 0, amount: 0 };
const defaultTheme = { primary: "#0b5fff", accent: "#111827" };

const currencyFmt = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(Number(n || 0));

/* ---------- tiny components ---------- */
function Modal({ open, onClose, width = 920, children }) {
  if (!open) return null;
  return (
    <div className="aip-m-portal">
      <div className="aip-m-backdrop" onClick={onClose} />
      <div className="aip-m-panel" style={{ width }}>
        <button className="aip-m-close" onClick={onClose}>✕</button>
        <div className="aip-m-body">{children}</div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="aip-stat">
      <div className="aip-stat-label">{label}</div>
      <div className="aip-stat-value">{value}</div>
    </div>
  );
}

/* =========================
   PAGE
   ========================= */
export default function AccountInvoicePage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest");
  const [mailing, setMailing] = useState(false);

  // modals
  const [showNew, setShowNew] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [emailTarget, setEmailTarget] = useState({ fileName: "", url: "" });
  const [emailTo, setEmailTo] = useState("");

  // form state for "New Invoice" modal
  const [org, setOrg] = useState({
    name: "Your Company",
    address: "Address",
    phone: "",
    email: "",
    gstin: "",
    pan: "",
    logoUrl: "",
    signatureUrl: "",
    theme: defaultTheme,
    bank: { name: "", accNo: "", ifsc: "", branch: "" },
    terms: [
      "Payment due within 7 days unless otherwise agreed.",
      "Please quote invoice number on payment."
    ],
  });
  const [customer, setCustomer] = useState({ name: "", email: "", address: "" });
  const [invoice, setInvoice] = useState({
    code: `INV-${dayjs().format("YYYYMMDD-HHmmss")}`,
    date: dayjs().format("YYYY-MM-DD"),
    notes: "",
    currency: "INR",
  });
  const [items, setItems] = useState([{ ...blankItem }]);
  const [taxPct, setTaxPct] = useState(18);
  const [emailNow, setEmailNow] = useState(false);
  const [emailNowTo, setEmailNowTo] = useState("");
  const [creating, setCreating] = useState(false);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, it) => {
      const qty = Number(it.qty || 0);
      const rate = Number(it.rate || 0);
      const amt = it.amount != null ? Number(it.amount) : qty * rate;
      return s + amt;
    }, 0);
    const taxAmt = (subtotal * Number(taxPct || 0)) / 100;
    return { subtotal, taxPct: Number(taxPct || 0), taxAmt, grandTotal: subtotal + taxAmt };
  }, [items, taxPct]);

  const setItem = (i, patch) => {
    setItems((arr) => {
      const copy = arr.slice();
      const row = { ...copy[i], ...patch };
      const qty = Number(row.qty || 0);
      const rate = Number(row.rate || 0);
      row.amount = +(qty * rate).toFixed(2);
      copy[i] = row;
      return copy;
    });
  };

  const addItem = () => setItems((a) => [...a, { ...blankItem }]);
  const removeItem = (i) => setItems((a) => a.filter((_, idx) => idx !== i));

  async function uploadImage(file) {
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch(`${Constants.API_URL}/accounts/upload`, { method: "POST", body: fd });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || "Upload failed");
    return data.url;
  }

  async function loadList() {
    setLoading(true);
    try {
      const r = await fetch(`${Constants.API_URL}/accounts/invoices`);
      const data = await r.json();
      if (r.ok) setList(data.rows || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadList(); }, []);

  const filtered = useMemo(() => {
    let rows = [...list];
    if (query.trim()) {
      const q = query.toLowerCase();
      rows = rows.filter(r => r.fileName.toLowerCase().includes(q));
    }
    rows.sort((a, b) => {
      if (sort === "newest") return new Date(b.mtime) - new Date(a.mtime);
      if (sort === "oldest") return new Date(a.mtime) - new Date(b.mtime);
      return a.fileName.localeCompare(b.fileName);
    });
    return rows;
  }, [list, query, sort]);

  const sendExisting = async (fileName, to) => {
    setMailing(true);
    try {
      const r = await fetch(`${Constants.API_URL}/accounts/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-role": "account" },
        body: JSON.stringify({ fileName, to }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Failed");
    } finally {
      setMailing(false);
    }
  };

  const createInvoice = async () => {
    setCreating(true);
    try {
      const body = { org, customer, invoice, items, totals, emailNow, emailTo: emailNowTo };
      const r = await fetch(`${Constants.API_URL}/accounts/invoice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-role": emailNow ? "account" : "guest",
        },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Failed");

      // refresh list and close
      await loadList();
      setShowNew(false);
      // reset form minimal
      setInvoice((x) => ({ ...x, code: `INV-${dayjs().format("YYYYMMDD-HHmmss")}`, date: dayjs().format("YYYY-MM-DD") }));
      setItems([{ ...blankItem }]);
      setEmailNow(false);
      setEmailNowTo("");
    } catch (e) {
      alert(e.message);
    } finally {
      setCreating(false);
    }
  };

  const totalSize = useMemo(
    () => (list.reduce((s, f) => s + (f.size || 0), 0) / 1024).toFixed(1) + " KB",
    [list]
  );

  return (
    <div className="aip-wrap">
      {/* header */}
      <div className="aip-toolbar" style={{ borderColor: "#e5e7eb" }}>
        <div className="aip-lhs">
          <h2 className="aip-title">Invoices</h2>
          <div className="aip-stats">
            <Stat label="Total Files" value={list.length} />
            <Stat label="Storage" value={totalSize} />
          </div>
        </div>
        <div className="aip-rhs">
          <input className="aip-search" placeholder="Search by file name…" value={query} onChange={(e)=>setQuery(e.target.value)} />
          <select className="aip-select" value={sort} onChange={(e)=>setSort(e.target.value)}>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="name">Name</option>
          </select>
          <button className="aip-btn aip-btn-ghost" onClick={loadList} disabled={loading}>{loading ? "Refreshing…" : "Refresh"}</button>
          <button className="aip-btn aip-btn-primary" onClick={()=>setShowNew(true)}>+ New Invoice</button>
        </div>
      </div>

      {/* list */}
      {filtered.length === 0 ? (
        <div className="aip-empty">
          <div className="aip-empty-card">
            <div className="aip-empty-icon">📄</div>
            <div className="aip-empty-title">No invoices yet</div>
            <div className="aip-empty-sub">Create your first invoice to see it here.</div>
            <button className="aip-btn aip-btn-primary" onClick={()=>setShowNew(true)}>Create Invoice</button>
          </div>
        </div>
      ) : (
        <div className="aip-grid-cards">
          {filtered.map((f) => (
            <div key={f.fileName} className="aip-card">
              <div className="aip-card-top">
                <div className="aip-file-icon">📄</div>
                <div className="aip-file-meta">
                  <div className="aip-file-name" title={f.fileName}>{f.fileName}</div>
                  <div className="aip-file-sub">
                    {(f.size/1024).toFixed(1)} KB • {dayjs(f.mtime).format("YYYY-MM-DD HH:mm")}
                  </div>
                </div>
              </div>
              <div className="aip-card-actions">
                <a className="aip-btn aip-btn-small" href={f.url} target="_blank" rel="noreferrer">Preview</a>
                <button className="aip-btn aip-btn-small aip-btn-ghost" onClick={() => { navigator.clipboard.writeText(f.url); }}>Copy link</button>
                <button
                  className="aip-btn aip-btn-small aip-btn-dark"
                  disabled={mailing}
                  onClick={() => { setEmailTarget({ fileName: f.fileName, url: f.url }); setShowEmail(true); }}
                >
                  {mailing ? "Sending…" : "Email"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ----- New Invoice Modal ----- */}
      <Modal open={showNew} onClose={()=>setShowNew(false)} width={980}>
        <h3 className="aip-modal-title">Create Invoice</h3>
        <div className="aip-two">
          <div>
            <h4>Organization</h4>
            <input placeholder="Company Name" value={org.name}
                   onChange={(e)=>setOrg({...org, name:e.target.value})}/>
            <textarea rows={3} placeholder="Address" value={org.address}
                      onChange={(e)=>setOrg({...org, address:e.target.value})}/>
            <div className="aip-row2">
              <input placeholder="Phone" value={org.phone} onChange={(e)=>setOrg({...org, phone:e.target.value})}/>
              <input placeholder="Email" value={org.email} onChange={(e)=>setOrg({...org, email:e.target.value})}/>
            </div>
            <div className="aip-row2">
              <input placeholder="GSTIN" value={org.gstin} onChange={(e)=>setOrg({...org, gstin:e.target.value})}/>
              <input placeholder="PAN"   value={org.pan}   onChange={(e)=>setOrg({...org, pan:e.target.value})}/>
            </div>

            <div className="aip-row2" style={{alignItems:"center"}}>
              <div>
                <label className="aip-lbl">Logo (upload)</label>
                <input type="file" accept="image/*"
                       onChange={async (e)=>{ const f=e.target.files?.[0]; if(!f) return;
                         const url = await uploadImage(f); setOrg({...org, logoUrl:url}); }}/>
                {org.logoUrl && <img src={org.logoUrl} alt="logo" style={{height:40, marginTop:6}}/>}
              </div>
              <div>
                <label className="aip-lbl">Signature (upload)</label>
                <input type="file" accept="image/*"
                       onChange={async (e)=>{ const f=e.target.files?.[0]; if(!f) return;
                         const url = await uploadImage(f); setOrg({...org, signatureUrl:url}); }}/>
                {org.signatureUrl && <img src={org.signatureUrl} alt="sign" style={{height:40, marginTop:6}}/>}
              </div>
            </div>

            <div className="aip-row2">
              <label>Brand <input type="color" value={org.theme.primary}
                     onChange={(e)=>setOrg({...org, theme:{...org.theme, primary:e.target.value}})} /></label>
              <label>Accent <input type="color" value={org.theme.accent}
                     onChange={(e)=>setOrg({...org, theme:{...org.theme, accent:e.target.value}})} /></label>
            </div>

            <h5 style={{marginTop:8}}>Bank</h5>
            <input placeholder="Bank Name" value={org.bank.name}
                   onChange={(e)=>setOrg({...org, bank:{...org.bank, name:e.target.value}})} />
            <div className="aip-row2">
              <input placeholder="A/C No" value={org.bank.accNo}
                     onChange={(e)=>setOrg({...org, bank:{...org.bank, accNo:e.target.value}})} />
              <input placeholder="IFSC" value={org.bank.ifsc}
                     onChange={(e)=>setOrg({...org, bank:{...org.bank, ifsc:e.target.value}})} />
            </div>
            <input placeholder="Branch" value={org.bank.branch}
                   onChange={(e)=>setOrg({...org, bank:{...org.bank, branch:e.target.value}})} />
          </div>

          <div>
            <h4>Customer</h4>
            <input placeholder="Name" value={customer.name}
                   onChange={(e)=>setCustomer({ ...customer, name:e.target.value })}/>
            <input placeholder="Email" value={customer.email}
                   onChange={(e)=>setCustomer({ ...customer, email:e.target.value })}/>
            <textarea rows={3} placeholder="Address" value={customer.address}
                      onChange={(e)=>setCustomer({ ...customer, address:e.target.value })}/>

            <h4 style={{marginTop:10}}>Invoice</h4>
            <input placeholder="Invoice Code" value={invoice.code}
                   onChange={(e)=>setInvoice({ ...invoice, code:e.target.value })}/>
            <input type="date" value={invoice.date}
                   onChange={(e)=>setInvoice({ ...invoice, date:e.target.value })}/>
            <textarea rows={3} placeholder="Notes" value={invoice.notes}
                      onChange={(e)=>setInvoice({ ...invoice, notes:e.target.value })}/>
          </div>
        </div>

        <h4 style={{ marginTop: 12 }}>Items</h4>
        <table className="aip-tbl">
          <thead>
            <tr><th align="left">Description</th><th>Qty</th><th>Rate</th><th>Amount</th><th/></tr>
          </thead>
          <tbody>
          {items.map((it, i) => (
            <tr key={i}>
              <td><input value={it.desc} onChange={(e)=>setItem(i,{desc:e.target.value})}/></td>
              <td><input type="number" step="0.01" value={it.qty}  onChange={(e)=>setItem(i,{qty:e.target.value})}/></td>
              <td><input type="number" step="0.01" value={it.rate} onChange={(e)=>setItem(i,{rate:e.target.value})}/></td>
              <td><input readOnly type="number" step="0.01" value={it.amount ?? 0}/></td>
              <td><button className="aip-btn aip-btn-tiny aip-btn-ghost" onClick={()=>removeItem(i)}>✕</button></td>
            </tr>
          ))}
          </tbody>
        </table>
        <button className="aip-btn aip-btn-ghost" onClick={addItem} style={{ marginTop: 8 }}>+ Add item</button>

        <div className="aip-totals">
          <div>Subtotal: <b>{currencyFmt(totals.subtotal)}</b></div>
          <div>Tax %: <input className="aip-in" type="number" step="0.01" value={taxPct} onChange={(e)=>setTaxPct(e.target.value)} /></div>
          <div>Tax: <b>{currencyFmt(totals.taxAmt)}</b></div>
          <div>Grand Total: <b className="aip-gt">{currencyFmt(totals.grandTotal)}</b></div>
        </div>

        <div className="aip-row2" style={{ alignItems: "center", marginTop: 12 }}>
          <label>
            <input type="checkbox" checked={emailNow} onChange={(e)=>setEmailNow(e.target.checked)} /> &nbsp;
            Email invoice now (requires account role)
          </label>
          {emailNow && (
            <input className="aip-in" placeholder="Send to (optional, uses Customer email if empty)"
                   style={{ flex: 1 }} value={emailNowTo} onChange={(e)=>setEmailNowTo(e.target.value)} />
          )}
        </div>

        <div className="aip-modal-actions">
          <button className="aip-btn aip-btn-ghost" onClick={()=>setShowNew(false)}>Cancel</button>
          <button className="aip-btn aip-btn-primary" onClick={createInvoice} disabled={creating}>
            {creating ? "Creating…" : "Create & Generate PDF"}
          </button>
        </div>
      </Modal>

      {/* ----- Email Modal ----- */}
      <Modal open={showEmail} onClose={()=>setShowEmail(false)} width={480}>
        <h3 className="aip-modal-title">Email Invoice</h3>
        <div className="aip-mini">
          <div className="aip-mini-row">
            <div className="aip-mini-label">File</div>
            <div className="aip-mini-val">{emailTarget.fileName}</div>
          </div>
          <div className="aip-mini-row">
            <div className="aip-mini-label">To</div>
            <input className="aip-in" placeholder="name@company.com" value={emailTo} onChange={(e)=>setEmailTo(e.target.value)} />
          </div>
          <div className="aip-modal-actions">
            <button className="aip-btn aip-btn-ghost" onClick={()=>setShowEmail(false)}>Close</button>
            <button
              className="aip-btn aip-btn-dark"
              disabled={mailing || !emailTo}
              onClick={async () => {
                await sendExisting(emailTarget.fileName, emailTo);
                setShowEmail(false);
                setEmailTo("");
              }}
            >
              {mailing ? "Sending…" : "Send Email"}
            </button>
          </div>
        </div>
      </Modal>

      {/* styles */}
      <style>{`
        .aip-wrap { max-width: 1100px; margin: 18px auto; padding: 10px 14px; }
        .aip-title { margin: 0 10px 0 0; }
        .aip-toolbar { display:flex; align-items:center; justify-content:space-between; border-bottom:1px solid; padding-bottom:10px; }
        .aip-lhs { display:flex; align-items:center; gap:16px; }
        .aip-rhs { display:flex; gap:10px; align-items:center; }
        .aip-stats { display:flex; gap:10px; }
        .aip-stat { background:var(--page-bg, #f8fafc); border:1px solid #eef2f7; padding:8px 10px; border-radius:10px; min-width:110px; }
        .aip-stat-label { font-size:12px; color:var(--text-muted, #64748b); }
        .aip-stat-value { font-weight:700; color:#14151f; }
        .aip-search { padding:10px 12px; border-radius:10px; border:1px solid #e5e7eb; width:220px; }
        .aip-select { padding:10px 12px; border-radius:10px; border:1px solid #e5e7eb; }
        .aip-btn { padding:10px 14px; border-radius:10px; border:none; background:#111827; color:#fff; cursor:pointer; }
        .aip-btn.aip-btn-primary { background:var(--primary, #15803d); }
        .aip-btn.aip-btn-dark { background:#111827; }
        .aip-btn.aip-btn-ghost { background:#eef2ff; color:#1e3a8a; }
        .aip-btn.aip-btn-small { padding:8px 10px; border-radius:9px; }
        .aip-btn.aip-btn-tiny { padding:6px 8px; }
        .aip-grid-cards { display:grid; grid-template-columns: repeat(auto-fill,minmax(280px,1fr)); gap:14px; margin-top:16px; }
        .aip-card { border:1px solid #e5e7eb; border-radius:14px; padding:12px; background:var(--surface, #fff); box-shadow: 0 1px 2px rgba(0,0,0,.03); display:flex; flex-direction:column; gap:10px; }
        .aip-card-top { display:flex; gap:10px; align-items:center; }
        .aip-file-icon { font-size:24px; }
        .aip-file-name { font-weight:700; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width: 100%; }
        .aip-file-sub { font-size:12px; color:var(--text-muted, #64748b); }
        .aip-card-actions { display:flex; gap:8px; }
        .aip-empty { display:flex; align-items:center; justify-content:center; padding:60px 0; }
        .aip-empty-card { text-align:center; background:var(--page-bg, #f8fafc); border:1px dashed var(--border-strong, #cbd5e1); border-radius:14px; padding:30px 22px; width:460px; }
        .aip-empty-icon { font-size:42px; }
        .aip-empty-title { font-weight:800; margin-top:8px; }
        .aip-empty-sub { color:var(--text-muted, #64748b); margin:6px 0 14px; }

        /* modal */
        .aip-m-portal { position:fixed; inset:0; z-index:50; }
        .aip-m-backdrop { position:absolute; inset:0; background:rgba(0,0,0,.36); backdrop-filter: blur(2px); }
        .aip-m-panel { position:absolute; left:50%; top:50%; transform:translate(-50%,-50%); background:var(--surface, #fff); border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.35); max-height:90vh; overflow:auto; }
        .aip-m-close { position:absolute; right:10px; top:10px; border:none; background:#f1f5f9; color:#14151f; border-radius:50%; width:32px; height:32px; cursor:pointer; }
        .aip-m-body { padding:18px; }
        .aip-modal-title { margin: 0 0 12px 0; }
        .aip-modal-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:14px; }

        /* forms inside modal */
        .aip-two { display:grid; grid-template-columns: 1fr 1fr; gap:14px; }
        .aip-row2 { display:flex; gap:10px; }
        .aip-lbl { display:block; font-size:12px; color:#6b7280; margin-bottom:4px; }
        input, textarea { width:100%; padding:10px; margin:6px 0; font-size:14px; border:1px solid #e5e7eb; border-radius:10px; }
        .aip-tbl { border-collapse:collapse; width:100%; }
        .aip-tbl th { background:#f3f4f6; text-transform:uppercase; font-size:12px; letter-spacing:.04em; }
        .aip-tbl th, .aip-tbl td { border-bottom:1px solid #eee; padding:8px; }
        .aip-totals { display:flex; gap:16px; align-items:center; flex-wrap:wrap; margin-top:10px; }
        .aip-in { padding:10px; border-radius:10px; border:1px solid #e5e7eb; }
        .aip-gt { color:#0b5fff; }

        /* mini email form */
        .aip-mini { padding-top:4px; }
        .aip-mini-row { display:flex; align-items:center; gap:10px; margin:10px 0; }
        .aip-mini-label { width:60px; font-size:13px; color:#6b7280; }
        .aip-mini-val { flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

        @media (max-width: 880px) { .aip-two { grid-template-columns:1fr; } .aip-m-panel { width:95vw !important; } }
      `}</style>
    </div>
  );
}
